import { cn } from "@/lib/utils";
import type {
  ActionGroup as ActionGroupType,
  Player,
  ActionType,
  ActionResult,
  RerollPreference,
} from "../types";
import { ActionGroup } from "./ActionGroup";
import { useLanguage } from "../i18n";

export interface ActionsSectionProps {
  groups: ActionGroupType[];
  players: Record<string, Player>;
  onAddGroup: () => void;
  onRemoveGroup: (groupId: string) => void;
  onSetGroupPlayer: (groupId: string, playerId: string | null) => void;
  onAddAction: (groupId: string) => void;
  onRemoveAction: (groupId: string, actionId: string) => void;
  onUpdateActionType: (groupId: string, actionId: string, type: ActionType) => void;
  onUpdateActionResult: (groupId: string, actionId: string, result: ActionResult) => void;
  onUpdateActionReroll: (groupId: string, actionId: string, reroll: RerollPreference) => void;
  className?: string;
}

export function ActionsSection({
  groups,
  players,
  onAddGroup,
  onRemoveGroup,
  onSetGroupPlayer,
  onAddAction,
  onRemoveAction,
  onUpdateActionType,
  onUpdateActionResult,
  onUpdateActionReroll,
  className,
}: ActionsSectionProps) {
  const { t } = useLanguage();

  return (
    <section
      className={cn(
        "bg-bb-gray-dark border-2 border-bb-gray-mid border-l-4 border-l-bb-blood-red mb-6",
        className
      )}
    >
      <div
        className={cn(
          "flex justify-between items-center",
          "bg-bb-gray-mid px-4 py-3",
          "border-b border-bb-gold/30"
        )}
      >
        <h2 className="font-condensed text-xl font-bold uppercase tracking-wider text-bb-gold">
          {t.actionSequence}
        </h2>
      </div>

      <div className="p-4">
        {groups.length === 0 ? (
          <p className="text-bb-bone-white/60 text-center py-4">
            {t.noActions}
          </p>
        ) : (
          groups.map((group) => (
            <ActionGroup
              key={group.id}
              group={group}
              players={players}
              onSetPlayer={(playerId) => onSetGroupPlayer(group.id, playerId)}
              onAddAction={() => onAddAction(group.id)}
              onRemoveAction={(actionId) => onRemoveAction(group.id, actionId)}
              onUpdateActionType={(actionId, type) => onUpdateActionType(group.id, actionId, type)}
              onUpdateActionResult={(actionId, result) => onUpdateActionResult(group.id, actionId, result)}
              onUpdateActionReroll={(actionId, reroll) => onUpdateActionReroll(group.id, actionId, reroll)}
              onRemove={() => onRemoveGroup(group.id)}
            />
          ))
        )}

        <button
          type="button"
          onClick={onAddGroup}
          className={cn(
            "w-full font-condensed text-base font-semibold uppercase tracking-wide",
            "px-4 py-3 mt-2 transition-all",
            "bg-bb-pitch-green text-bb-bone-white border-2 border-bb-pitch-dark",
            "hover:bg-bb-pitch-dark hover:border-bb-gold"
          )}
        >
          {t.addActionGroup}
        </button>
      </div>
    </section>
  );
}
